import { IMatch } from '../matches/typings';

// Who owns the video — mirrors FootData\Entity\MatchVideo::META_IMPORT_SOURCE_ADVERSPORT and
// the FDI provider names. Imports only update/prune their own videos; anything created or
// edited here is stamped Adversport.
export const MATCH_VIDEO_SOURCE_ADVERSPORT = 'Adversport';
export const MATCH_VIDEO_SOURCE_YOUTUBE = 'youtube';
export const MATCH_VIDEO_SOURCE_SCOREBAT = 'scorebat';
export type MatchVideoSource =
  | typeof MATCH_VIDEO_SOURCE_ADVERSPORT
  | typeof MATCH_VIDEO_SOURCE_YOUTUBE
  | typeof MATCH_VIDEO_SOURCE_SCOREBAT
  | string;

export const MATCH_VIDEO_TITLES = ['Highlights', 'Extended'] as const;
export type MatchVideoTitle = typeof MATCH_VIDEO_TITLES[number];

export interface IMatchVideoThumbnail {
  width: number | null;
  height: number | null;
  url: string;
}

export interface IMatchVideo {
  '@id'?: string;
  id: string;
  title: string;
  // Always the YouTube embed URL once saved (normalised by the API).
  url: string;
  match?: IMatch;
  originalTitle?: string | null;
  originalDescription?: string | null;
  thumbnails?: { [format: string]: IMatchVideoThumbnail } | null;
  sourcePublishedAt?: string | null;
  // ISO 8601 duration (PT3M35S).
  duration?: string | null;
  isEmbeddable?: boolean | null;
  metaImportSource: MatchVideoSource;
  ordering: number;
  createdAt?: string;
  updatedAt?: string;
}

// Payload accepted by the API: the metadata are resolved server-side from the YouTube URL.
export interface IMatchVideoPayload {
  url: string;
  match: string; // IRI
  title: MatchVideoTitle;
}

// Best thumbnail for a small preview (320x180 when available).
export function getPreviewThumbnail(video: IMatchVideo): IMatchVideoThumbnail | null {
  const thumbnails = video.thumbnails || {};
  for (const format of ['medium', 'high', 'default', 'standard', 'maxres']) {
    if (thumbnails[format] && thumbnails[format].url) return thumbnails[format];
  }
  return null;
}

// PT1H2M3S -> 1:02:03, PT3M35S -> 3:35
export function formatDuration(duration?: string | null): string {
  if (!duration) return '-';
  const parts = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(duration);
  if (!parts) return duration;
  const [, h, m, s] = parts;
  const pad = (n: string | undefined) => String(Number(n || 0)).padStart(2, '0');
  return h ? `${Number(h)}:${pad(m)}:${pad(s)}` : `${Number(m || 0)}:${pad(s)}`;
}
